import {
	FlowSegmentStep,
	JourneyAttribute,
	JourneyFilterState,
	JourneyFlowLink,
	JourneyFlowNode,
	JourneyFlowResult,
	JourneyMeta,
	JourneySegment,
	TimeBetweenResult,
} from '../../../models/journey-explorer/journey-explorer';
import {
	formatSpan,
	selectedCustomerValues,
	selectedDimensionValues,
	toggleCustomerValue,
	toggleDimensionValue,
} from './journey-explorer-utils';

const CHURN_STATUS_COLUMN = 'CHURN_STATUS';

export interface AttributeOption {
	key: string;
	label: string;
	attribute: JourneyAttribute;
}

export const attributeKey = ({ table, column }: JourneyAttribute): string => `${table ?? ''}.${column}`;

export const attributeOptions = (meta: JourneyMeta): AttributeOption[] => [
	...meta.customer_dimensions
		.filter((dimension) => dimension.column !== CHURN_STATUS_COLUMN)
		.map((dimension) => ({
			key: attributeKey({ table: null, column: dimension.column }),
			label: dimension.label,
			attribute: { table: null, column: dimension.column },
		})),
	...meta.interaction_types.flatMap((type) =>
		type.dimensions.map((dimension) => ({
			key: attributeKey(dimension),
			label: `${type.label}: ${dimension.label}`,
			attribute: { table: dimension.table, column: dimension.column },
		}))
	),
];

export const keepAttributeValue = (
	state: JourneyFilterState,
	{ table, column }: JourneyAttribute,
	value: string
): JourneyFilterState => {
	if (table === null) {
		return selectedCustomerValues(state, column).includes(value)
			? state
			: toggleCustomerValue(state, column, value);
	}
	return selectedDimensionValues(state, table, column).includes(value)
		? state
		: toggleDimensionValue(state, table, column, value);
};

const keptNames = (flow: JourneyFlowResult, step: number): string[] =>
	flow.nodes
		.filter((node) => node.step === step && node.kind === 'interaction' && node.name !== null)
		.map((node) => node.name as string);

const earlierSteps = (flow: JourneyFlowResult, upTo: number): FlowSegmentStep[] =>
	Array.from({ length: Math.max(0, upTo) }, (_, index) => ({ op: 'in', names: keptNames(flow, index + 1) }));

const nodeStep = (flow: JourneyFlowResult, node: JourneyFlowNode): FlowSegmentStep | null => {
	switch (node.kind) {
		case 'interaction':
			return { op: 'in', names: [node.name as string] };
		case 'other':
			return { op: 'not_in', names: keptNames(flow, node.step) };
		case 'stop':
			return { op: 'stop', names: [] };
		default:
			return null;
	}
};

export const isEndNode = (node: JourneyFlowNode): boolean => node.kind === 'end' || node.kind === 'not_end';

const flowSegment = (flow: JourneyFlowResult, path: FlowSegmentStep[], reached?: boolean): JourneySegment => ({
	kind: 'flow',
	flow: {
		direction: flow.direction,
		anchor: flow.anchor,
		steps: flow.steps,
		within: flow.within,
		path,
		...(flow.direction === 'between' && { end: flow.end ?? null }),
		...(reached !== undefined && { reached }),
	},
});

const sourceStep = (flow: JourneyFlowResult, node: JourneyFlowNode): FlowSegmentStep[] => {
	if (node.kind === 'interaction') {
		return [{ op: 'in', names: [node.name as string] }];
	}
	if (node.kind === 'other') {
		return [{ op: 'not_in', names: keptNames(flow, node.step) }];
	}
	return [];
};

export const flowNodeSegment = (flow: JourneyFlowResult, node: JourneyFlowNode): JourneySegment => {
	if (isEndNode(node)) {
		return flowSegment(flow, [], node.kind === 'end');
	}
	const last = nodeStep(flow, node);
	return flowSegment(flow, last ? [...earlierSteps(flow, node.step - 1), last] : []);
};

const endLinkSegment = (flow: JourneyFlowResult, source: JourneyFlowNode, target: JourneyFlowNode): JourneySegment => {
	const stopped = source.step < flow.steps && source.kind !== 'other';
	return flowSegment(
		flow,
		[
			...earlierSteps(flow, source.step - 1),
			...sourceStep(flow, source),
			...(stopped ? [{ op: 'stop' as const, names: [] }] : []),
		],
		target.kind === 'end'
	);
};

export const flowLinkSegment = (flow: JourneyFlowResult, link: JourneyFlowLink): JourneySegment | null => {
	const source = flow.nodes.find((node) => node.key === link.source);
	const target = flow.nodes.find((node) => node.key === link.target);
	if (source && target && isEndNode(target)) {
		return endLinkSegment(flow, source, target);
	}
	const last = target ? nodeStep(flow, target) : null;
	if (!source || !target || !last) {
		return null;
	}
	return flowSegment(flow, [...earlierSteps(flow, target.step - 2), ...sourceStep(flow, source), last]);
};

export const isTimeable = (segment: JourneySegment): boolean => {
	if (segment.kind === 'transition') {
		return true;
	}
	if (segment.kind !== 'flow') {
		return false;
	}
	const { path, reached } = segment.flow;
	return reached === true || (path.length > 0 && path[path.length - 1].op !== 'stop');
};

export interface TimeSummary {
	sentence: string;
	advice: string;
}

export const timeSummary = (result: TimeBetweenResult): TimeSummary | null => {
	const { p25_secs: low, median_secs: median, p75_secs: high } = result;
	if (low === null || median === null || high === null || result.customers === 0) {
		return null;
	}
	return {
		sentence: `Half of these customers make the move between ${formatSpan(low)} and ${formatSpan(
			high
		)}, with a median of ${formatSpan(median)}.`,
		advice: `To change the outcome, step in before ${formatSpan(low)}, when most customers have not moved yet.`,
	};
};
